import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { X, Sparkles } from 'lucide-react';
import { useAds } from '../contexts/AdContext';
import AdSenseSlot from './AdSenseSlot';
import EzoicSlot from './EzoicSlot';
import { activeWebAdProvider } from '../lib/webAdProvider';

/**
 * AdPopup — Modal that displays simulated ads to non-premium users.
 *
 * UX:
 *  - 15 second countdown before the close button becomes active.
 *    Matches Google's non-skippable interstitial standard (max 15s) so the
 *    house ad feels like a real network interstitial — and gives a genuine
 *    reason to buy ad-free Premium.
 *  - Primary CTA leads to a real in-app action (e.g., premium upgrade)
 *  - "Skip" appears after countdown ends
 */
const CLOSE_DELAY_S = 15;

export default function AdPopup({ onAction }) {
  const { showAd, closeAd: closeAdCtx, suspendAd } = useAds();
  const [secondsLeft, setSecondsLeft] = useState(CLOSE_DELAY_S);

  const closeAd = (action = null) => {
    closeAdCtx();
    if (action && onAction) onAction(action);
  };

  // 2026-07 ad-skip exploit fix — mid-countdown clicks (premium link / house
  // CTA) no longer dismiss the ad: they SUSPEND it with the remaining seconds
  // and it resumes at the next trigger point unless the user went premium.
  const suspendToAction = (action) => {
    suspendAd(secondsLeft);
    if (action && onAction) onAction(action);
  };

  useEffect(() => {
    if (!showAd) return;
    setSecondsLeft(showAd.resumeSeconds ?? CLOSE_DELAY_S);
    const interval = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(interval);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
    // v5.7.92 — Intentionally key the timer reset on `showAd?.id` only.
    // We don't want a new countdown every time `showAd`'s object identity
    // changes (e.g. parent re-renders with the same ad); only when the
    // CURRENT ad swaps for a different one. The `if (!showAd) return`
    // guard above handles the dismiss case.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAd?.id]);

  if (!showAd) return null;

  const canClose = secondsLeft === 0;
  const provider = activeWebAdProvider();          // 'ezoic' | 'adsense' | 'house' | 'none'
  const isNetworkAd = provider === 'ezoic' || provider === 'adsense';

  return (
    <Dialog
      open={!!showAd}
      onOpenChange={(open) => {
        if (!open && canClose) closeAd();
      }}
    >
      <DialogContent
        className="max-w-sm border-2 p-0 overflow-hidden gap-0 [&>button]:hidden"
        data-testid="ad-popup"
        onPointerDownOutside={(e) => { if (!canClose) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (!canClose) e.preventDefault(); }}
      >
        {/* Tiny "Advertisement" label — legal/clarity */}
        <div className="flex items-center justify-between px-3 py-1.5 bg-secondary/60 border-b border-border/60">
          <span className="text-[9px] uppercase tracking-widest text-muted-foreground font-semibold flex items-center gap-1">
            <Sparkles className="h-2.5 w-2.5" /> Advertisement
          </span>
          <button
            onClick={() => canClose && closeAd()}
            disabled={!canClose}
            className={`h-6 w-6 rounded-full flex items-center justify-center transition-all ${
              canClose ? 'hover:bg-secondary cursor-pointer' : 'opacity-50 cursor-not-allowed'
            }`}
            data-testid="ad-popup-close-btn"
            aria-label="Close ad"
          >
            {canClose ? (
              <X className="h-3.5 w-3.5" />
            ) : (
              <span className="text-[10px] font-bold tabular-nums">{secondsLeft}</span>
            )}
          </button>
        </div>

        {/* Skip-countdown progress bar — mimics a real interstitial's timed
            "skip in Ns" bar so the house ad reads as a genuine ad. */}
        <div className="h-0.5 w-full bg-secondary/40">
          <div
            className="h-full bg-accent transition-all duration-1000 ease-linear"
            style={{ width: `${((CLOSE_DELAY_S - secondsLeft) / CLOSE_DELAY_S) * 100}%` }}
            data-testid="ad-popup-progress"
          />
        </div>

        {/* Ad body — render REAL network ad (Ezoic / AdSense) if configured, else simulated creative */}
        {isNetworkAd ? (
          <div className="p-4">
            <DialogTitle className="sr-only">Advertisement</DialogTitle>
            <DialogDescription className="sr-only">Sponsored content from our ad partners</DialogDescription>
            {provider === 'ezoic' ? <EzoicSlot /> : <AdSenseSlot />}
            <div className="mt-4 text-center">
              {canClose ? (
                <button
                  onClick={() => closeAd()}
                  className="text-xs text-muted-foreground underline hover:text-foreground"
                  data-testid="ad-popup-skip-btn"
                >
                  Continue
                </button>
              ) : (
                <p className="text-[10px] text-muted-foreground">
                  Skip in {secondsLeft}s • <button type="button" className="underline cursor-pointer hover:text-foreground" data-testid="ad-popup-remove-ads-link" onClick={() => suspendToAction('premium')}>Remove ads forever</button>
                </p>
              )}
            </div>
          </div>
        ) : (
        <div
          className="p-6 text-center"
          style={{
            background: `linear-gradient(135deg, ${showAd.accent}15 0%, transparent 80%)`,
          }}
        >
          <DialogTitle className="text-xl font-bold mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
            {showAd.title}
          </DialogTitle>
          <DialogDescription className="text-sm text-foreground/80 mb-5">
            {showAd.body}
          </DialogDescription>

          <Button
            onClick={() => (canClose ? closeAd(showAd.ctaAction) : suspendToAction(showAd.ctaAction))}
            className="w-full"
            style={{ background: showAd.accent }}
            data-testid="ad-popup-cta-btn"
          >
            {showAd.cta}
          </Button>

          {!canClose && (
            <p className="text-[10px] text-muted-foreground mt-3">
              Skip in {secondsLeft}s • <button type="button" className="underline cursor-pointer hover:text-foreground" data-testid="ad-popup-remove-ads-link" onClick={() => suspendToAction('premium')}>Remove ads forever</button>
            </p>
          )}
          {canClose && (
            <button
              onClick={() => closeAd()}
              className="text-[10px] text-muted-foreground mt-3 underline hover:text-foreground"
              data-testid="ad-popup-skip-btn"
            >
              No thanks, continue
            </button>
          )}
        </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
